import { useMemo, type ReactNode } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  EditableCollectionBinding,
  EditableSlotGroup,
  EditableToken,
  useCollectionBinding,
  useContentToken,
  useSlotGroup,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

interface Amenity {
  title?: string | null;
  description?: string | null;
  group?: string | null;
  amenity_icon?: string | null;
  amenity_image?: string | null;
  amenity_image_alt_text?: string | null;
  is_featured?: string | null;
  [key: string]: unknown;
}

interface CategoryConfig {
  group: string;
  label: string;
}

// ============================================================================
// Category photos — one round image above each amenity group heading.
// Image selection mirrors AmenitiesForYou: pick from photo_gallery a shot
// that visually represents the category (existing photos only). Definitions
// are fully inlined for static schema extraction.
// ============================================================================

const indoorPhotoDef: SlotGroupDefinition = {
  label: 'Indoor Amenities Photo',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'photo_gallery[0].image',
      generator: {
        prompt:
          'From photo_gallery only, pick the existing photo whose subject best represents Indoor Amenities (e.g. fitness center, lounge, study space, clubhouse interior). The image must depict that category — not a generic or unrelated space. Never invent an image.',
      },
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'photo_gallery[0].alt_text',
      generator: {
        prompt:
          'Alt text for the selected gallery photo that names the indoor amenity shown.',
      },
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: {
      image: 'image',
      alt: 'alt_text',
    },
  },
  generator: {
    prompt:
      'Choose a photo_gallery image that clearly shows an Indoor Amenity for this property. Prefer photos that visually match indoor common-area amenities. Skip unrelated shots.',
    context:
      'Amenities list category photo 1 of 4 — Indoor Amenities. Each category must use a different gallery photo.',
  },
};

const inHomePhotoDef: SlotGroupDefinition = {
  label: 'In-Home Features Photo',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'photo_gallery[1].image',
      generator: {
        prompt:
          'From photo_gallery only, pick the existing photo whose subject best represents In-Home Features (e.g. apartment interior, kitchen, living area, bedroom). The image must depict that category — not a generic or unrelated space. Never invent an image.',
      },
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'photo_gallery[1].alt_text',
      generator: {
        prompt:
          'Alt text for the selected gallery photo that names the in-home feature shown.',
      },
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 1,
    fieldMappings: {
      image: 'image',
      alt: 'alt_text',
    },
  },
  generator: {
    prompt:
      'Choose a photo_gallery image that clearly shows an In-Home Feature for this property. Prefer apartment interior photos that match unit finishes and features. Skip unrelated shots.',
    context:
      'Amenities list category photo 2 of 4 — In-Home Features. Each category must use a different gallery photo.',
  },
};

const outdoorPhotoDef: SlotGroupDefinition = {
  label: 'Outdoor Amenities Photo',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'photo_gallery[2].image',
      generator: {
        prompt:
          'From photo_gallery only, pick the existing photo whose subject best represents Outdoor Amenities (e.g. pool, courtyard, patio, outdoor lounge). The image must depict that category — not a generic or unrelated space. Never invent an image.',
      },
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'photo_gallery[2].alt_text',
      generator: {
        prompt:
          'Alt text for the selected gallery photo that names the outdoor amenity shown.',
      },
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 2,
    fieldMappings: {
      image: 'image',
      alt: 'alt_text',
    },
  },
  generator: {
    prompt:
      'Choose a photo_gallery image that clearly shows an Outdoor Amenity for this property. Prefer pool, patio, or courtyard photos. Skip unrelated shots.',
    context:
      'Amenities list category photo 3 of 4 — Outdoor Amenities. Each category must use a different gallery photo.',
  },
};

const neighborhoodPhotoDef: SlotGroupDefinition = {
  label: 'Neighborhood Photo',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'photo_gallery[3].image',
      generator: {
        prompt:
          'From photo_gallery only, pick the existing photo whose subject best represents the Neighborhood category (e.g. building exterior, streetscape, nearby area). The image must depict that category — not a generic or unrelated space. Never invent an image.',
      },
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'photo_gallery[3].alt_text',
      generator: {
        prompt:
          'Alt text for the selected gallery photo that names the neighborhood or exterior scene shown.',
      },
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 3,
    fieldMappings: {
      image: 'image',
      alt: 'alt_text',
    },
  },
  generator: {
    prompt:
      'Choose a photo_gallery image that clearly represents the Neighborhood around this property. Prefer exterior, streetscape, or nearby-area photos. Skip unrelated shots.',
    context:
      'Amenities list category photo 4 of 4 — Neighborhood. Each category must use a different gallery photo.',
  },
};

/** Amenity categories in display order. Indoor pairs with In-Home, Outdoor
 *  with Neighborhood — reads as interior vs exterior across the grid. */
const CATEGORIES: CategoryConfig[] = [
  { group: 'Indoor Amenities', label: 'Indoor Amenities' },
  { group: 'In-Home Features', label: 'In-Home Features' },
  { group: 'Outdoor Amenities', label: 'Outdoor Amenities' },
  { group: 'Neighborhood', label: 'Neighborhood' },
];

function hasSelectUnitsMarker(title: string | null | undefined): boolean {
  return typeof title === 'string' && title.includes('*');
}

function CategoryPhoto({
  image,
  alt,
  label,
}: {
  image?: string | null;
  alt?: string | null;
  label: string;
}) {
  const src = (image || '').trim();
  const imgAlt = (alt || label || '').trim();

  return (
    <div className="amenities-col-photo">
      {src ? (
        <VibesitesImage
          url={src}
          alt={imgAlt}
          sizes="(min-width: 1024px) 224px, (min-width: 640px) 208px, 192px"
          fill
          objectFit="cover"
          className="amenities-col-photo-img"
        />
      ) : (
        <div className="amenities-col-photo-img amenities-col-photo-img--empty" aria-hidden="true" />
      )}
    </div>
  );
}

interface AmenityColumnProps {
  category: CategoryConfig;
  items: Amenity[];
  index: number;
  photo: ReactNode;
  /** Shown under In-Home Features when that column has asterisk-marked amenities. */
  disclaimer?: string;
}

function AmenityColumn({
  category,
  items,
  index,
  photo,
  disclaimer,
}: AmenityColumnProps) {
  const colRef = useScrollReveal<HTMLElement>();
  const disclaimerText = disclaimer || '* Available in select units';

  return (
    <article
      ref={colRef}
      className="reveal-fade-up amenities-col"
      style={{ transitionDelay: `${index * 80}ms` }}
    >
      {photo}
      <h3 className="amenities-col-heading">{category.label}</h3>
      <ul className="amenities-col-list">
        {items.map((item, i) => (
          <li key={`${item.title ?? 'item'}-${i}`} className="amenities-col-item">
            {item.title || ''}
          </li>
        ))}
      </ul>
      {disclaimer != null ? (
        <EditableToken
          path="amenitieslist.select_units_disclaimer"
          as="p"
          className="amenities-list-disclaimer"
        >
          {disclaimerText}
        </EditableToken>
      ) : null}
    </article>
  );
}

export function AmenitiesList() {
  const amenities = useCollectionBinding<Amenity>('amenitieslist.items', {
    collection: 'amenities',
  });
  const selectUnitsDisclaimer = useContentToken('amenitieslist.select_units_disclaimer');
  const indoorPhoto = useSlotGroup('amenitieslist.indoor_photo', indoorPhotoDef);
  const inHomePhoto = useSlotGroup('amenitieslist.in_home_photo', inHomePhotoDef);
  const outdoorPhoto = useSlotGroup('amenitieslist.outdoor_photo', outdoorPhotoDef);
  const neighborhoodPhoto = useSlotGroup(
    'amenitieslist.neighborhood_photo',
    neighborhoodPhotoDef,
  );

  const itemsByGroup = useMemo(() => {
    const map = new Map<string, Amenity[]>();
    for (const category of CATEGORIES) {
      map.set(
        category.group,
        amenities.filter((item) => item.group?.trim() === category.group),
      );
    }
    return map;
  }, [amenities]);

  const visibleCategories = useMemo(
    () =>
      CATEGORIES.map((category) => ({
        category,
        items: itemsByGroup.get(category.group) ?? [],
      })).filter((entry) => entry.items.length > 0),
    [itemsByGroup],
  );

  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();

  if (visibleCategories.length === 0) return null;

  const indoorItems = itemsByGroup.get('Indoor Amenities') ?? [];
  const inHomeItems = itemsByGroup.get('In-Home Features') ?? [];
  const outdoorItems = itemsByGroup.get('Outdoor Amenities') ?? [];
  const neighborhoodItems = itemsByGroup.get('Neighborhood') ?? [];

  const showIndoor = indoorItems.length > 0;
  const showInHome = inHomeItems.length > 0;
  const showOutdoor = outdoorItems.length > 0;
  const showNeighborhood = neighborhoodItems.length > 0;

  const showDisclaimer =
    showInHome && inHomeItems.some((item) => hasSelectUnitsMarker(item.title));

  let columnIndex = 0;

  return (
    <section ref={sectionRef} className="amenities-list reveal-fade-up bg-white">
      <EditableCollectionBinding bindingPath="amenitieslist.items" label="Amenities">
        <div className="amenities-list-inner">
          <div
            className={`amenities-list-grid amenities-list-grid--${Math.min(visibleCategories.length, 4)}`}
          >
            {showIndoor ? (
              <AmenityColumn
                category={CATEGORIES[0]}
                items={indoorItems}
                index={columnIndex++}
                photo={
                  <EditableSlotGroup
                    groupPath="amenitieslist.indoor_photo"
                    definition={indoorPhotoDef}
                    className="amenities-col-photo-slot"
                  >
                    <CategoryPhoto
                      image={indoorPhoto.image}
                      alt={indoorPhoto.alt}
                      label="Indoor Amenities"
                    />
                  </EditableSlotGroup>
                }
              />
            ) : null}

            {showInHome ? (
              <AmenityColumn
                category={CATEGORIES[1]}
                items={inHomeItems}
                index={columnIndex++}
                photo={
                  <EditableSlotGroup
                    groupPath="amenitieslist.in_home_photo"
                    definition={inHomePhotoDef}
                    className="amenities-col-photo-slot"
                  >
                    <CategoryPhoto
                      image={inHomePhoto.image}
                      alt={inHomePhoto.alt}
                      label="In-Home Features"
                    />
                  </EditableSlotGroup>
                }
                disclaimer={
                  showDisclaimer
                    ? selectUnitsDisclaimer || '* Available in select units'
                    : undefined
                }
              />
            ) : null}

            {showOutdoor ? (
              <AmenityColumn
                category={CATEGORIES[2]}
                items={outdoorItems}
                index={columnIndex++}
                photo={
                  <EditableSlotGroup
                    groupPath="amenitieslist.outdoor_photo"
                    definition={outdoorPhotoDef}
                    className="amenities-col-photo-slot"
                  >
                    <CategoryPhoto
                      image={outdoorPhoto.image}
                      alt={outdoorPhoto.alt}
                      label="Outdoor Amenities"
                    />
                  </EditableSlotGroup>
                }
              />
            ) : null}

            {showNeighborhood ? (
              <AmenityColumn
                category={CATEGORIES[3]}
                items={neighborhoodItems}
                index={columnIndex++}
                photo={
                  <EditableSlotGroup
                    groupPath="amenitieslist.neighborhood_photo"
                    definition={neighborhoodPhotoDef}
                    className="amenities-col-photo-slot"
                  >
                    <CategoryPhoto
                      image={neighborhoodPhoto.image}
                      alt={neighborhoodPhoto.alt}
                      label="Neighborhood"
                    />
                  </EditableSlotGroup>
                }
              />
            ) : null}
          </div>
        </div>
      </EditableCollectionBinding>

      {/* Keep schema wrappers mounted when a category column is filtered out. */}
      {!showIndoor ? (
        <div className="amenities-list-photo-fallbacks" hidden aria-hidden="true">
          <EditableSlotGroup
            groupPath="amenitieslist.indoor_photo"
            definition={indoorPhotoDef}
          >
            <span>{indoorPhoto.image || ''}</span>
          </EditableSlotGroup>
        </div>
      ) : null}
      {!showInHome ? (
        <div className="amenities-list-photo-fallbacks" hidden aria-hidden="true">
          <EditableSlotGroup
            groupPath="amenitieslist.in_home_photo"
            definition={inHomePhotoDef}
          >
            <span>{inHomePhoto.image || ''}</span>
          </EditableSlotGroup>
        </div>
      ) : null}
      {!showOutdoor ? (
        <div className="amenities-list-photo-fallbacks" hidden aria-hidden="true">
          <EditableSlotGroup
            groupPath="amenitieslist.outdoor_photo"
            definition={outdoorPhotoDef}
          >
            <span>{outdoorPhoto.image || ''}</span>
          </EditableSlotGroup>
        </div>
      ) : null}
      {!showNeighborhood ? (
        <div className="amenities-list-photo-fallbacks" hidden aria-hidden="true">
          <EditableSlotGroup
            groupPath="amenitieslist.neighborhood_photo"
            definition={neighborhoodPhotoDef}
          >
            <span>{neighborhoodPhoto.image || ''}</span>
          </EditableSlotGroup>
        </div>
      ) : null}

      {/* Schema-lint anchor: EditableToken only mounts when In-Home shows the disclaimer. */}
      {!showDisclaimer ? (
        <EditableToken
          path="amenitieslist.select_units_disclaimer"
          as="span"
          className="hidden"
          style={{ display: 'none' }}
        >
          {''}
        </EditableToken>
      ) : null}

      <style>{`
        .amenities-list {
          width: 100%;
        }

        .amenities-list-inner {
          max-width: 80rem;
          margin: 0 auto;
          padding: 3.5rem 1.5rem 4.5rem;
        }
        @media (min-width: 768px) {
          .amenities-list-inner {
            padding: 4.5rem 2rem 5.5rem;
          }
        }
        @media (min-width: 1024px) {
          .amenities-list-inner {
            padding: 5rem 2.5rem 6rem;
          }
        }

        .amenities-list-grid {
          display: grid;
          gap: 3.75rem 2.75rem;
          align-items: start;
        }
        @media (min-width: 640px) {
          .amenities-list-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 4rem 3.5rem;
          }
        }
        @media (min-width: 1024px) {
          .amenities-list-grid--4 {
            grid-template-columns: repeat(4, minmax(0, 1fr));
            gap: 3.5rem 3rem;
          }
          .amenities-list-grid--3 {
            grid-template-columns: repeat(3, minmax(0, 1fr));
            gap: 3.75rem 3.25rem;
          }
          .amenities-list-grid--1 {
            grid-template-columns: minmax(0, 18rem);
            justify-content: center;
          }
          .amenities-list-grid--2 {
            grid-template-columns: repeat(2, minmax(0, 18rem));
            justify-content: center;
            gap: 4rem 5rem;
          }
        }

        .amenities-col {
          display: flex;
          flex-direction: column;
          align-items: stretch;
          min-width: 0;
        }

        .amenities-col-photo-slot {
          display: flex;
          justify-content: center;
          margin: 0 auto 1.5rem;
          width: 100%;
          max-width: 100%;
        }

        .amenities-col-photo {
          position: relative;
          width: min(12rem, 100%);
          border-radius: 50%;
          overflow: hidden;
          background: var(--vibe-color-surface);
          flex-shrink: 0;
        }
        /* In-flow square so fill images (absolute) don't collapse the circle. */
        .amenities-col-photo::before {
          content: '';
          display: block;
          padding-bottom: 100%;
        }
        @media (min-width: 640px) {
          .amenities-col-photo {
            width: min(13rem, 100%);
          }
        }
        @media (min-width: 1024px) {
          .amenities-col-photo {
            width: min(14rem, 100%);
          }
        }

        .amenities-col-photo-img {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center;
          display: block;
        }
        .amenities-col-photo-img--empty {
          background: color-mix(in srgb, var(--vibe-color-primary) 8%, var(--vibe-color-surface));
        }

        .amenities-col-heading {
          font-family: var(--vibe-font-heading);
          font-size: 1.375rem;
          font-weight: 700;
          line-height: 1.25;
          color: var(--vibe-color-text);
          margin: 0 0 0.55rem;
          text-align: left;
          /* Prefer one line; wrap only when the column is too narrow. */
          text-wrap: pretty;
        }
        @media (min-width: 1024px) {
          .amenities-col-heading {
            font-size: 1.5rem;
          }
        }

        .amenities-col-list {
          list-style: disc;
          list-style-position: outside;
          margin: 0;
          padding: 0 0 0 1.15rem;
          width: 100%;
          text-align: left;
        }

        .amenities-col-item {
          font-family: var(--vibe-font-body);
          font-size: 0.9375rem;
          color: var(--vibe-color-text);
          line-height: 1.55;
          padding: 0.2rem 0;
        }
        @media (min-width: 640px) {
          .amenities-col-item {
            font-size: 1rem;
          }
        }
        .amenities-col-item::marker {
          color: var(--vibe-color-text);
        }

        .amenities-list-disclaimer {
          font-family: var(--vibe-font-body);
          font-size: 0.8125rem;
          line-height: 1.5;
          color: var(--vibe-color-text);
          margin: 0.85rem 0 0;
          opacity: 0.75;
        }
        @media (min-width: 640px) {
          .amenities-list-disclaimer {
            font-size: 0.875rem;
            margin-top: 1rem;
          }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'AmenitiesList',
  displayName: 'Amenities List',
  tokens: {
    'amenitieslist.select_units_disclaimer': {
      label: 'Select Units Disclaimer',
      type: 'text',
      defaultValue: '* Available in select units',
    },
  },
  slotGroups: {
    'amenitieslist.indoor_photo': indoorPhotoDef,
    'amenitieslist.in_home_photo': inHomePhotoDef,
    'amenitieslist.outdoor_photo': outdoorPhotoDef,
    'amenitieslist.neighborhood_photo': neighborhoodPhotoDef,
  },
  collectionBindings: {
    'amenitieslist.items': {
      label: 'Amenities',
      collection: 'amenities',
    },
  },
};

(AmenitiesList as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default AmenitiesList;
